import { useAuthStore } from '@/auth/sessionStore';
import {
  catalogMutationErrorMessage,
  createAndEnableInvestigation,
  createLocalDraftInvestigation,
  investigationCreateErrorMessage } from
'@/modules/lab/catalog/api/catalogMaintenanceApi';
import { enableAtHospital } from '@/modules/lab/catalog/api/catalogPilotApi';
import { AliasTagInput } from '@/modules/lab/catalog/components/AliasTagInput';
import { InlineDedupSuggestions } from '@/modules/lab/catalog/components/InlineDedupSuggestions';
import { CategorySelectorField, SpecimenSelectorField } from '@/modules/lab/catalog/components/catalogSelectors';
import { useCatalogPilot } from '@/modules/lab/catalog/context/CatalogPilotContext';
import { useDedupCheck } from '@/modules/lab/catalog/hooks/useDedupCheck';
import type { MatchCandidate } from '@/modules/lab/catalog/catalogDedup';
import { useQuickCreateStore } from '@/modules/lab/catalog/quickCreateStore';
import { categoryHelperFor, getPopularTests } from '@/modules/lab/catalog/quickCreateHelpers';
import { getOperatorPrefs } from '@/modules/lab/catalog/catalogOperatorPrefs';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox } from
'@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function hasEnterpriseMasterPermission(permissions: string[]): boolean {
  const set = new Set(permissions.map((p) => p.toUpperCase()));
  return set.has('FONE:LABMASTERENTERPRISE') || process.env.NODE_ENV === 'development';
}

type Props = {
  onSaved?: () => void;
};

export function QuickCreateDrawer({ onSaved }: Props) {
  const { open, initial, closeDrawer } = useQuickCreateStore();
  const { rows, refresh } = useCatalogPilot();
  const hospitalCode = useAuthStore((s) => s.appsCode ?? s.user?.appsCode ?? 'HOSP');
  const permissions = useAuthStore((s) => s.user?.permissionList ?? []);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [specimen, setSpecimen] = useState('');
  const [billable, setBillable] = useState(true);
  const [aliases, setAliases] = useState<string[]>([]);
  const [testCode, setTestCode] = useState('');
  const [asLocal, setAsLocal] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [forceCreate, setForceCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [specimenTouched, setSpecimenTouched] = useState(false);
  const [prefsTick, setPrefsTick] = useState(0);

  const resetForm = useCallback(() => {
    setName(initial.name ?? '');
    setCategory(initial.category?.toUpperCase() ?? '');
    setSpecimen('');
    setAsLocal(initial.localVariant ?? true);
    setAliases([]);
    setTestCode('');
    setForceCreate(false);
    setError(null);
    setNotice(null);
    setCategoryTouched(false);
    setSpecimenTouched(false);
    setAdvancedOpen(false);
    setBillable(true);
  }, [initial]);

  useEffect(() => {
    if (!open) return;
    resetForm();
    const t = window.setTimeout(() => nameInputRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, [open, resetForm]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        nameInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const dedupQuery = useMemo(() => ({ name, aliases, shortCode: testCode }), [name, aliases, testCode]);
  const { matches, checking, hasHighConfidenceMatch } = useDedupCheck(dedupQuery, rows, open);
  const canOverride = hasEnterpriseMasterPermission(permissions);

  const categoryError = categoryTouched && !category ? 'Select a department — do not leave blank' : undefined;
  const specimenError = specimenTouched && !specimen ? 'Select specimen type — required for safe ordering' : undefined;

  const blockCreate = hasHighConfidenceMatch && !forceCreate && !asLocal;

  const popular = useMemo(() => getPopularTests(rows), [rows]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps (preserve behavior)
  const recentTests = useMemo(() => getOperatorPrefs().recentTests, [prefsTick, open]);

  const buildInput = () => ({
    displayName: name.trim(),
    category,
    specimenType: specimen,
    billableFlag: billable,
    aliases,
    investigationCode: testCode.trim(),
    hospitalCode,
    asLocalVariant: asLocal
  });

  const validate = (): boolean => {
    if (!name.trim()) {
      setError('Enter the test name');
      return false;
    }
    if (!testCode.trim()) {
      setError('Enter a test code');
      return false;
    }
    setCategoryTouched(true);
    setSpecimenTouched(true);
    if (!category) {
      setError('Select a test category');
      return false;
    }
    if (!specimen) {
      setError('Select a specimen type');
      return false;
    }
    if (blockCreate) {
      setError('Similar test exists — enable it or create a hospital-only variant');
      return false;
    }
    return true;
  };

  const handleEnableExisting = async (match: MatchCandidate) => {
    const row = rows.find((r) => r.investigationMasterId === match.masterId);
    if (!row) return;
    setSaving(true);
    setError(null);
    try {
      if (!row.hospitalEnabled) await enableAtHospital(row);
      await refresh();
      onSaved?.();
      closeDrawer();
    } catch (e) {
      setError(catalogMutationErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const runCreate = async (mode: 'enable' | 'draft') => {
    if (!validate()) return;
    setSaving(true);
    setError(null);
    try {
      const created = mode === 'enable'
        ? await createAndEnableInvestigation(buildInput())
        : await createLocalDraftInvestigation(buildInput());
      setPrefsTick((t) => t + 1);
      await refresh();
      onSaved?.();
      if (created.reactivated) {
        setNotice('Investigation reactivated.');
      }
      closeDrawer();
    } catch (e) {
      setError(investigationCreateErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const applyChip = (testName: string, cat?: string) => {
    setName(testName);
    if (cat) setCategory(cat);
    nameInputRef.current?.focus();
  };

  const queryLength = name.trim().length + aliases.join('').length + testCode.trim().length;

  return (
    <>
    {notice ? (
      <Alert severity="success" sx={{ m: 2 }} onClose={() => setNotice(null)}>
        {notice}
      </Alert>
    ) : null}
    <Drawer
      anchor="right"
      open={open}
      onClose={closeDrawer}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column'
        }
      }}>

      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800}>
            Quick add test
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Hospital: <strong>{hospitalCode}</strong> · press <kbd>/</kbd> to focus name
          </Typography>
        </Box>
        <IconButton size="small" onClick={closeDrawer} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
          {(recentTests.length > 0 || popular.length > 0) &&
        <div className="mb-3 space-y-2">
              {recentTests.length > 0 &&
          <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Recently added
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {recentTests.map((t) =>
              <Chip
                key={t.masterId}
                size="small"
                label={t.name}
                variant="outlined"
                onClick={() => applyChip(t.name)}
                sx={{ fontSize: '0.7rem' }} />

              )}
                  </div>
                </div>
          }
              {popular.length > 0 &&
          <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Popular at hospital
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {popular.slice(0, 6).map((r) =>
              <Chip
                key={r.investigationMasterId}
                size="small"
                label={r.displayName}
                variant="outlined"
                onClick={() => applyChip(r.displayName, r.category)}
                sx={{ fontSize: '0.7rem' }} />

              )}
                  </div>
                </div>
          }
            </div>
        }

          <Box
          component="fieldset"
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50',
            px: 1.5,
            py: 1.5,
            m: 0
          }}
          className="space-y-2">

            <legend className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Test identification
            </legend>
            <TextField
            inputRef={nameInputRef}
            id="qc-name"
            size="small"
            fullWidth
            required
            label="Test name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Complete haemogram, CBC"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.metaKey) void runCreate('enable');
            }} />

            <TextField
            id="qc-code"
            size="small"
            fullWidth
            required
            label="Test code"
            value={testCode}
            onChange={(e) => setTestCode(e.target.value)}
            placeholder="e.g. BIL-TOTAL"
            helperText="Letters, numbers, hyphens, and underscores. This is the investigation code."
            inputProps={{ maxLength: 64, style: { fontFamily: 'monospace', fontSize: '0.8rem' }, 'aria-label': 'Test code' }} />


            <InlineDedupSuggestions
            matches={forceCreate ? [] : matches}
            checking={checking}
            queryLength={queryLength}
            canOverride={canOverride}
            onEnableExisting={(m) => void handleEnableExisting(m)}
            onCreateLocalVariant={() => {
              setAsLocal(true);
              setForceCreate(true);
            }}
            onContinueAnyway={() => setForceCreate(true)} />

          </Box>

          <Box
          component="fieldset"
          sx={{
            mt: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50',
            px: 1.5,
            py: 1.5,
            m: 0
          }}
          className="space-y-2">

            <legend className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Classification
            </legend>
            <CategorySelectorField
            label="Department / category"
            required
            value={category}
            prefsVersion={prefsTick}
            onChange={(v) => {
              setCategory(v);
              setCategoryTouched(true);
              setPrefsTick((t) => t + 1);
            }}
            placeholder="Select department…"
            helperText={categoryHelperFor(category)}
            error={categoryError} />

            <SpecimenSelectorField
            label="Specimen type"
            required
            value={specimen}
            prefsVersion={prefsTick}
            onChange={(v) => {
              setSpecimen(v);
              setSpecimenTouched(true);
              setPrefsTick((t) => t + 1);
            }}
            placeholder="Select specimen type…"
            error={specimenError} />

          </Box>

          <Box
          component="fieldset"
          sx={{
            mt: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50',
            px: 1.5,
            py: 1.5,
            m: 0
          }}
          className="space-y-1.5">

            <legend className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Billing
            </legend>
            <FormControlLabel
            control={<Checkbox size="small" checked={billable} onChange={(e) => setBillable(e.target.checked)} />}
            label={<Typography variant="body2">Billable when ordered at this hospital</Typography>} />

          </Box>

          <Button
          fullWidth
          size="small"
          color="primary"
          variant="text"
          sx={{ mt: 1 }}
          onClick={() => setAdvancedOpen((v) => !v)}
          startIcon={advancedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}>

            Aliases & codes
          </Button>

          {advancedOpen &&
        <div className="space-y-2 rounded-md border border-dashed px-2.5 py-2">
              <AliasTagInput
            aliases={aliases}
            onChange={setAliases}
            placeholder="Type or paste aliases (comma / line)" />

              <FormControlLabel
            control={<Checkbox size="small" checked={asLocal} onChange={(e) => setAsLocal(e.target.checked)} />}
            label={<Typography variant="caption">Hospital-only (recommended for new tests)</Typography>} />

            </div>
        }

          {error &&
        <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
        }
      </Box>

      <Box
        sx={{
          px: 2,
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.06)'
        }}>

        <Stack spacing={1}>
          <Button
            fullWidth
            variant="contained"
            disabled={saving || !name.trim() || !testCode.trim()}
            onClick={() => void runCreate('enable')}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>

            Create and enable
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              variant="outlined"
              disabled={saving || !name.trim() || !testCode.trim()}
              onClick={() => void runCreate('draft')}>

              Save draft
            </Button>
            <Button variant="text" disabled={saving} onClick={closeDrawer}>
              Cancel
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
            ⌘ Enter to create and enable
          </Typography>
        </Stack>
      </Box>
    </Drawer>
    </>);

}